"use client";

import { NextIntlClientProvider } from "next-intl";
import { usePathname, useSearchParams } from "next/navigation";
import React, { useEffect, useRef } from "react";
import { SWRConfig } from "swr";
import { ApplicationState } from "../../components/ApplicationState";
import { ConsentProvider } from "../../components/Consent/provider";
import { DayjsProvider } from "../../components/Dayjs";
import { sendError } from "../../components/ErrorBoundary/helpers";
import { pushDataLayer } from "../../helpers/dataLayer";
import { getDayjsLocale } from "../../helpers/dayjsLocales";
import { extractCountry } from "../../helpers/generic";
import { getWindow } from "../../helpers/getWindow";
import { isNavigatorConnectionSupported } from "../../helpers/isNavigatorConnectionSupported";
import { coreWebVitals } from "../../helpers/webVitals";
import { swrConfig } from "../config.generic";

type Props = {
    children: React.ReactNode;
};

const Providers: React.FC<Props> = ({ children }) => {
    const pathname = usePathname();
    const window = getWindow();
    const searchParams = useSearchParams();
    const country = extractCountry(
        window?.location.hostname ?? global.hostname,
        pathname,
        searchParams
    );

    // For webview we don't want to track web vitals.
    // Use search params as useIsWebview is declared in this component by ApplicationState provider.
    const initWebVitals = useRef(searchParams.get("webview") === "app");
    const dayjsLocale = getDayjsLocale(country);

    useEffect(() => {
        if (window !== undefined) {
            pushDataLayer({
                type: "event",
                event: "savedata",
                eventCategory: "Save Data Check",
                eventAction: isNavigatorConnectionSupported(window.navigator)
                    ? window.navigator.connection.saveData
                    : "unsupported",
                eventLabel: window.navigator.userAgent,
                nonInteraction: true,
            });

            if (!initWebVitals.current) {
                coreWebVitals();
                initWebVitals.current = true;
            }
        }
    }, [window]);

    useEffect(() => {
        // this has to be done here, as the next Document is ssr only
        window?.newrelic?.setApplicationVersion(
            process.env.NEXT_PUBLIC_BUILD_NUMBER ?? "unknown"
        );

        if (typeof window?.newrelic?.addRelease !== "undefined") {
            window.newrelic.addRelease(
                `web-app-react-${
                    process.env.NEXT_PUBLIC_DEPLOY_ENV ?? "unknown"
                }`,
                process.env.NEXT_PUBLIC_BUILD_NUMBER ?? "unknown"
            );
        }
    }, [window?.newrelic]);

    return (
        <NextIntlClientProvider
            onError={(error) => sendError(error)}
            getMessageFallback={({ namespace, key }) =>
                `${namespace ?? "unknown"}.${key}`
            }
            locale={country}
        >
            <SWRConfig value={swrConfig}>
                <DayjsProvider locale={dayjsLocale}>
                    <ApplicationState>
                        <ConsentProvider>{children}</ConsentProvider>
                    </ApplicationState>
                </DayjsProvider>
            </SWRConfig>
        </NextIntlClientProvider>
    );
};

export default Providers;
